import { put, call, delay, select } from 'redux-saga/effects';
import { overtimeConstants } from './OvertimeConstants';
import { getCallsheet, addOTShift, resolveWbFailed, postCallLog, getCallLog, getVotReportData, getVotReportDetailData, getVotCallsheetData, checkOTShiftStatus, checkDuplicateShift, getOtSignups, getSessionStatusData } from './index';
import { getOTSkillsData } from '../OT-Comp/index';
import * as actions from '../OT-Comp/OvertimeAction';
import { getStationTimestamp } from '../VTO/utils';
import {
    loadDraft,
    persistConfirmedDecision,
    clearPersistedDecision,
    clearAllPersistedDecisions,
} from './draftPersistence';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const toIsoTimestamp = (value) => {
    if (!value) return new Date().toISOString();

    const raw = String(value).trim();
    if (!raw) return new Date().toISOString();

    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString();
    }

    const fallbackParsed = new Date(raw);
    if (!Number.isNaN(fallbackParsed.getTime())) {
        return fallbackParsed.toISOString();
    }

    return new Date().toISOString();
};

const toHourMinute = (value = '') => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    // ISO 8601: "2026-07-31T06:00:00Z" or "...T06:00:00.000Z"
    // Space-separated: "2026-07-31 06:00:00"
    // Plain time: "06:00" or "06:00:00"
    let timePortion;
    if (raw.includes('T')) {
        timePortion = raw.split('T')[1].replace(/Z$/, '');
    } else if (raw.includes(' ')) {
        timePortion = raw.split(' ')[1];
    } else {
        timePortion = raw;
    }
    const parts = timePortion.split(':');
    if (parts.length < 2) return '';
    const [hour, minute] = parts;
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};

export function* getOvertimeData(action) {
    try {
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });
        const response = yield call(getCallsheet, action.params);
        
        // Check for API-level errors returned with HTTP 200
        const responseData = response?.data;
        if (responseData?.errorCode || responseData?.session?.errorCode) {
            const errorMessage = responseData?.message || responseData?.session?.message || 'An error occurred. Please try again.';
            yield put({ 
                type: overtimeConstants.GET_OVERTIME_DATA_FAILED, 
                data: errorMessage
            });
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            return;
        }

        const data = responseData?.data || responseData || response;
        const sessionToken = data?.session?.sessionToken;
        if (sessionToken) {
            localStorage.setItem('ot_sessionToken', sessionToken);
        }

        yield put({ 
            type: overtimeConstants.GET_OVERTIME_DATA_SUCCESS, 
            data: response.data,
            persistedDecisions: loadDraft('ot_draft_confirmedDecisions', {}),
            requestParams: action.params || {}
        });
        
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
       
    } catch (error) {
        yield put({ 
            type: overtimeConstants.GET_OVERTIME_DATA_FAILED, 
            data: error.message || 'Failed to fetch overtime data' 
        });
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
     
    }
}

export function* getSessionStatusSaga(action) {
    try {
        const response = yield call(getSessionStatusData, action.params || {});
        const responseData = response?.data || response;
        const data = responseData?.data || responseData || {};
        const sessionToken = data?.sessionToken;

        if (sessionToken) {
            localStorage.setItem('ot_sessionToken', sessionToken);
        }

        yield put({
            type: overtimeConstants.GET_SESSION_STATUS_SUCCESS,
            data,
            requestParams: action.params || {},
        });
    } catch (error) {
        yield put({
            type: overtimeConstants.GET_SESSION_STATUS_FAILED,
            data: error.message || 'Failed to fetch session status',
        });
    }
}

export function* confirmOvertimeDecision(action) {
    const { employeeId, shiftName, signupId, decision, shiftData } = action;
    const retryMessage = 'Request failed. Please try again.';
    yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });

    const sessionToken = yield select(state => state.OvertimeReducer?.callSheetData?.sessionToken) || localStorage.getItem('ot_sessionToken') || '';

    for(let i = 0; i < MAX_RETRIES; i++) {
        try {
            const requestBody = {
                empId: employeeId,
                votRequestId: shiftData?.votRequestId || '',
                station: shiftData?.station || '',
                shiftDate: shiftData?.shiftDate || '',
                otStartTime: shiftData?.otStartTime || '',
                otEndTime: shiftData?.otEndTime || '',
                shiftType: shiftData?.shiftType || '',
                workLocation: shiftData?.workLocation || '',
                schedBidGroup: shiftData?.schedBidGroup || '',
                otGroup: shiftData?.otGroup || '',
                unpaidLunch: (shiftData?.unpaidLunch === 'Y' || shiftData?.unpaidLunch === true || (typeof shiftData?.unpaidLunch === 'string' && shiftData.unpaidLunch.toUpperCase() === 'U30')) ? 'Y' : 'N',
                overlapAllowed: shiftData?.overlapAllowed || 'N',
                requestType: ({ ACCEPT: 'APPLY', ACCEPTED: 'APPLY', REFUSE: 'REFUSE', REFUSED: 'REFUSE', 'SAVE-ACCEPTED': 'SAVE-ACCEPTED', 'SAVE-REFUSED': 'SAVE-REFUSED' }[(decision || '').toUpperCase()] || decision),
                reason: shiftData?.otCode || '',
                transactionId: shiftData?.transactionId || null,
                transactionTime: toIsoTimestamp(shiftData?.transactionTime),
                firstName: shiftData?.firstName || '',
                lastName: shiftData?.lastName || '',
                unionSeniority: shiftData?.unionSeniority || '',
                sessionToken,
            };

            const response = yield call(addOTShift, requestBody);
            if (response?.status === 200 || response?.data?.statusCode === 'SUCCESS' || response?.data?.result?.status === 'SUCCESS') {
                // Detect error-in-200: API returned HTTP 200 but result is null/empty with an error message
                // (e.g. Workbrain 500). Treat as WB_FAILED so the admin can resolve via the resolve modal.
                if (!response?.data?.result && response?.data?.message) {
                    const station = shiftData?.station || '';
                    let lastTimeStamp = '';
                    try {
                        lastTimeStamp = yield call(getStationTimestamp, station);
                    } catch (e) {
                        lastTimeStamp = new Date().toISOString();
                    }

                    yield put({
                        type: overtimeConstants.CONFIRM_DECISION_SUCCESS,
                        employeeId,
                        shiftName,
                        signupId,
                        decision: 'WB_FAILED',
                        lastTimeStamp,
                        error: response.data.message,
                        savedAt: new Date().toISOString(),
                    });

                    yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
                    return;
                }

                const station = shiftData?.station || '';
                let lastTimeStamp = '';
                try {
                    lastTimeStamp = yield call(getStationTimestamp, station);
                } catch (e) {
                    lastTimeStamp = new Date().toISOString();
                }
                // Extract transactionId/reqId from /shift API response, matched by aaId
                const resultArr = Array.isArray(response?.data?.result) ? response.data.result : [];
                // Find the result entry whose employeeData matches this employee
                let transactionId = '';
                let reqId = '';
                for (const resultEntry of resultArr) {
                    const employeeData = Array.isArray(resultEntry?.employeeData) ? resultEntry.employeeData : [];
                    const matched = employeeData.find((e) => String(e.aaId) === String(employeeId));
                    if (matched) {
                        transactionId = String(matched.transactionId || '');
                        reqId = resultEntry?.reqId || transactionId;
                        break;
                    }
                }
                // Fallback: first entry if none matched by aaId
                if (!reqId && resultArr.length > 0) {
                    const first = resultArr[0];
                    transactionId = String(first?.employeeData?.[0]?.transactionId || '');
                    reqId = first?.reqId || transactionId;
                }

                const isWbSuccess = (decision || '').replace(/[_\s]/g, '').toUpperCase() === 'WBSUCCESS';
                const nextStatus = isWbSuccess ? 'ASSIGNED' : decision;

                yield put({
                    type: overtimeConstants.CONFIRM_DECISION_SUCCESS,
                    employeeId,
                    shiftName,
                    signupId,
                    decision,
                    lastTimeStamp,
                    reqId,
                    transactionId,
                    startTime: toHourMinute(shiftData?.startTime || shiftData?.otStartTime),
                    endTime: toHourMinute(shiftData?.endTime || shiftData?.otEndTime),
                    workLocation: shiftData?.workLocation || '',
                    savedAt: new Date().toISOString(),
                });

                // Persist/clear decision in session storage (side effect outside reducer)
                if (!isWbSuccess) {
                    persistConfirmedDecision(signupId, nextStatus, reqId, transactionId, lastTimeStamp);
                } else {
                    clearPersistedDecision(signupId);
                }

                yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
                return;
            }

            throw new Error(response?.data?.message || retryMessage);
        } catch (error) {
            if (i < MAX_RETRIES - 1) {
                yield delay(RETRY_DELAY);
                continue;
            }

            yield put({
                type: overtimeConstants.CONFIRM_DECISION_FAILED,
                employeeId,
                shiftName,
                signupId,
                data: retryMessage
            });
            
            yield put({
                type: overtimeConstants.ADD_OT_SHIFT_FAILED,
                employeeId,
                shiftName,
                signupId,
                data: retryMessage
            });
            
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            return;
        }
    }
}

export function* resolveOTWbFailedSaga(action) {
    const { employeeId, shiftName, signupId, resolution } = action;
    try {
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });

        const response = yield call(resolveWbFailed, {
            votRequestId: resolution.votRequestId,
            empId: resolution.empId,
            shiftType: resolution.shiftType,
            station: resolution.station,
            startTime: resolution.startTime,
            endTime: resolution.endTime,
            workLocation: resolution.workLocation,
            wbFailureResolveAction: resolution.wbFailureResolveAction,
            wbFailureResolveComments: resolution.wbFailureResolveComments,
        });

        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.RESOLVE_OT_WB_FAILED_SUCCESS,
                employeeId,
                shiftName,
                signupId,
                resolveAction: resolution.wbFailureResolveAction,
                resolvedAt: new Date().toISOString(),
            });
        } else {
            yield put({
                type: overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED,
                employeeId,
                shiftName,
                signupId,
                data: response?.data?.message || 'Failed to resolve WB Failed request.',
            });
        }

        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
    } catch (error) {
        yield put({
            type: overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED,
            employeeId,
            shiftName,
            signupId,
            data: error.message || 'Failed to resolve WB Failed request.',
        });
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
    }
}

export function* getOTSkillsDataSaga(data) {
    for(let i = 0; i < MAX_RETRIES; i++) {
        try {
            const response = yield call(
                getOTSkillsData,
                data.params,
            )
            
            if (response?.status === 200) {
                yield put(actions.getOTSkillsDataSuccess(true))
                const skillsData = response.data?.otskills || response.data?.skills || response.data || [];
                yield put(actions.getOTSkillsResponse(skillsData))
                return; // Success - exit the function
            } else {
                console.error("ERROR: " + response?.status);
                if (i === MAX_RETRIES - 1) {
                    yield put(actions.getOTSkillsDataSuccess(false))
                } else {
                    yield delay(RETRY_DELAY);
                }
            }
        }
        catch (error) {
            console.error("ERROR: " + error);
            if (i === MAX_RETRIES - 1) {
                // Last retry attempt failed
                yield put(actions.getOTSkillsDataSuccess(false))
            } else {
                yield delay(RETRY_DELAY);
            }
        }
    }
}

export function* advanceToNextRequirementSaga() {
    try {
        const callSheetData = yield select((state) => state.OvertimeReducer.callSheetData);
        const requestParams = callSheetData?.requestParams;
        const session = callSheetData?.session;
        const sessionToken = callSheetData?.sessionToken;
        const currentShift = callSheetData?.callSheetWorkingShift?.current || '';

        if (!requestParams || !currentShift) {
            return;
        }

        // Filter out the fulfilled requirement by matching shiftType to current shift name
        const updatedShiftsReqs = (requestParams.shiftsReqs || []).filter(
            (req) => (req.shiftType || '').toUpperCase() !== currentShift.toUpperCase()
        );

        const updatedParams = {
            ...requestParams,
            shiftsReqs: updatedShiftsReqs,
            sessionToken,
            isCurrentCompleted: true,
        };

        // Reset fulfillment flag before fetching
        yield put({ type: overtimeConstants.RESET_SLOT_FULFILLED });

        // Re-fetch callsheet with remaining requirements
        yield put({ type: overtimeConstants.GET_OVERTIME_DATA, params: updatedParams });
    } catch (error) {
        console.error('Error advancing to next requirement:', error);
    }
}

/**
 * Fetches the VOT OT report for a given station, department, and date.
 * Dispatches GET_VOT_REPORT_SUCCESS with the report data on success,
 * or GET_VOT_REPORT_FAILED on error.
 */
export function* getVotReportSaga(action) {
    try {
        const response = yield call(getVotReportData, action.params);
        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.GET_VOT_REPORT_SUCCESS,
                data: response.data,
            });
        } else {
            yield put({
                type: overtimeConstants.GET_VOT_REPORT_FAILED,
                data: response?.data?.message || 'Failed to fetch VOT report',
            });
        }
    } catch (error) {
        yield put({
            type: overtimeConstants.GET_VOT_REPORT_FAILED,
            data: error.message || 'Failed to fetch VOT report',
        });
    }
}

/**
 * Fetches the VOT report detail for a specific VOT ID.
 * Dispatches GET_VOT_REPORT_DETAIL_SUCCESS with the report data on success,
 * or GET_VOT_REPORT_DETAIL_FAILED on error.
 */
export function* getVotReportDetailSaga(action) {
    try {
        const response = yield call(getVotReportDetailData, {
            votId: action.votId,
            shiftType: action.shiftType,
            otStartTime: action.otStartTime,
            otEndTime: action.otEndTime,
        });
        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.GET_VOT_REPORT_DETAIL_SUCCESS,
                data: response.data && Array.isArray(response.data) ? response.data[0] : response.data,
            });
        } else {
            yield put({
                type: overtimeConstants.GET_VOT_REPORT_DETAIL_FAILED,
                data: response?.data?.message || 'Failed to fetch VOT report detail',
            });
        }
    } catch (error) {
        yield put({
            type: overtimeConstants.GET_VOT_REPORT_DETAIL_FAILED,
            data: error.message || 'Failed to fetch VOT report detail',
        });
    }
}

export function* getVotCallsheetSaga(action) {
    try {
        const response = yield call(getVotCallsheetData, action.votId);
        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.GET_VOT_CALLSHEET_SUCCESS,
                data: response.data,
            });
        } else {
            yield put({
                type: overtimeConstants.GET_VOT_CALLSHEET_FAILED,
                data: response?.data?.message || 'Failed to fetch VOT call sheet',
            });
        }
    } catch (error) {
        yield put({
            type: overtimeConstants.GET_VOT_CALLSHEET_FAILED,
            data: error.message || 'Failed to fetch VOT call sheet',
        });
    }
}

/**
 * Polls /addOTShift/status with pending reqIds to check WB confirmation status.
 * Keeps polling every 60 seconds until all reqIds are resolved, then marks
 * polling complete and lets the UI wait for explicit Continue action.
 * Falls back to persisted reqIds from session storage when Redux state
 * doesn't have them (e.g., after page refresh).
 */
const WB_POLL_INTERVAL = 60 * 1000; // 60 seconds
const WB_POLL_MAX_ATTEMPTS = 30; // safety cap: stop after 30 minutes

export function* pollWbStatusSaga() {
    try {
        for (let attempt = 0; attempt < WB_POLL_MAX_ATTEMPTS; attempt++) {
            const overtimeState = yield select((state) => state.OvertimeReducer);
            const callSheetData = overtimeState?.callSheetData;
            const shiftSignups = overtimeState?.shiftSignups;
            const currentShift = callSheetData?.callSheetWorkingShift?.current || '';

            if (!currentShift || !shiftSignups?.byId) return;

            // Collect reqIds from current-shift signups that are still pending in Workbrain.
            // This includes both apply and refuse flows while wbStatus remains WB_PENDING.
            const pendingReqIds = [];
            Object.values(shiftSignups.byId).forEach((signup) => {
                if ((signup.shiftName || '').toUpperCase() !== currentShift.toUpperCase()) return;
                const status = String(signup.status || '').replace(/[\s_-]/g, '').toUpperCase();
                const selectedDecision = (signup.selectedDecision || '').toUpperCase();
                const normalizedWbStatus = String(signup.wbStatus || '').replace(/[\s_]/g, '').toUpperCase();
                const isWbSuccess = signup.wbStatus === true || normalizedWbStatus === 'WBSUCCESS';
                const isWbFailed = normalizedWbStatus === 'WBFAILED';
                const shouldPollAcceptedPending = status === 'ACCEPT' && !isWbSuccess && !isWbFailed;
                const shouldPollRefusedPending =
                    (status === 'REFUSE' || status === 'REFUSED' || selectedDecision === 'REFUSE' || selectedDecision === 'REFUSED') &&
                    !isWbSuccess && !isWbFailed;

                if (shouldPollAcceptedPending || shouldPollRefusedPending) {
                    const id = signup.reqId || signup.transactionId || '';
                    if (id) pendingReqIds.push(id);
                }
            });

            // Fallback: if no reqIds in Redux, check session storage for persisted decisions
            if (pendingReqIds.length === 0) {
                const persisted = loadDraft('ot_draft_confirmedDecisions', {});
                Object.values(persisted).forEach((decision) => {
                    const persistedStatus = String(decision.status || '').replace(/[\s_-]/g, '').toUpperCase();
                    if (persistedStatus === 'ACCEPT' || persistedStatus === 'REFUSE' || persistedStatus === 'REFUSED') {
                        const id = decision.reqId || decision.transactionId || '';
                        if (id) pendingReqIds.push(id);
                    }
                });
            }

            if (pendingReqIds.length === 0) {
                yield put({ type: overtimeConstants.POLL_WB_STATUS_SUCCESS, results: {}, allResolved: true });
                return;
            }

            const response = yield call(checkOTShiftStatus, pendingReqIds.join(','));
            const results = Array.isArray(response?.data?.result) ? response.data.result : [];

            // Build status map: reqId → { status, comment }
            const statusMap = {};
            results.forEach((r) => {
                if (r.reqId) {
                    statusMap[String(r.reqId)] = { status: r.status, comment: r.comment || '' };
                }
            });

            // Check if all pending reqIds have been resolved (not still pending)
            const allResolved = pendingReqIds.every((id) => {
                const result = statusMap[String(id)];
                return result && (result.status === 'APPLIED' || result.status === 'WB_SUCCESS' || result.status === 'WB_FAILURE' || result.status === 'WB_FAILED');
            });

            yield put({
                type: overtimeConstants.POLL_WB_STATUS_SUCCESS,
                results: statusMap,
                allResolved,
            });

            // Clear persisted decisions for signups that reached terminal states
            // and trigger call-log + OT signup refresh for each resolved employee
            const updatedState = yield select((state) => state.OvertimeReducer?.shiftSignups?.byId || {});
            const votId = callSheetData?.callSheetId || '';
            const resolvedEmployeeIds = new Set();

            Object.keys(updatedState).forEach((signupId) => {
                const signup = updatedState[signupId];
                const id = String(signup.reqId || signup.transactionId || '');
                if (!id || !statusMap[id]) return;
                const result = statusMap[id];
                if (result.status === 'APPLIED' || result.status === 'WB_SUCCESS' || result.status === 'WB_FAILURE') {
                    clearPersistedDecision(signupId);
                    if (signup.employeeId) resolvedEmployeeIds.add(signup.employeeId);
                }
            });

            // Fetch call-log and OT signups for each resolved employee
            for (const empId of resolvedEmployeeIds) {
                yield put({ type: overtimeConstants.GET_CALL_LOG, votId, empId });
                yield put({ type: overtimeConstants.GET_OT_SIGNUPS, empId, employeeId: empId });
            }

            // If all resolved, stop polling
            if (allResolved) return;

            // Wait before next poll attempt
            yield delay(WB_POLL_INTERVAL);
        }

        // Safety cap reached — stop polling
        yield put({ type: overtimeConstants.POLL_WB_STATUS_SUCCESS, results: {}, allResolved: true });
    } catch (error) {
        console.error('Poll WB status failed:', error);
        yield put({
            type: overtimeConstants.POLL_WB_STATUS_SUCCESS,
            results: {},
            allResolved: false,
        });
    }
}

/**
 * Fire-and-forget POST to call-log API.
 * Logs actions (button clicks) and call entries.
 */
export function* postCallLogSaga(action) {
    try {
        const sessionToken = yield select(state => state.OvertimeReducer?.callSheetData?.sessionToken) || localStorage.getItem('ot_sessionToken') || '';
        const requestDepartment = yield select((state) => state.OvertimeReducer?.callSheetData?.requestParams?.department || '');
        const department = action?.data?.department || requestDepartment || '';
        yield call(postCallLog, { ...action.data, sessionToken, department });
        yield put({ type: overtimeConstants.POST_CALL_LOG_SUCCESS });
        if (action.data?.holdMeta) {
            yield put({
                type: overtimeConstants.SET_NO_CONTACT_HOLD,
                hold: { ...action.data.holdMeta, startedAt: action.data.holdMeta.startedAt || Date.now() },
            });
        }

        const callType = String(action?.data?.callType || '').toUpperCase();
        if (callType === 'OUTBOUND' || callType === 'INBOUND') {
            yield put({ type: overtimeConstants.IS_CALLED_SUCCESS, data: action.data});
        }
        // Fetch updated call log from API so NoContact holds are applied to the
        // first eligible current-shift signup immediately after saving.
        const empId = action.data?.empId;
        const votId = action.data?.votRequestId;
        if (empId && votId) {
            yield put({ type: overtimeConstants.GET_CALL_LOG, votId, empId });
        }
    } catch (error) {
        // Silent failure — logging should not block the user
        console.error('Post call log failed:', error);
        yield put({ type: overtimeConstants.POST_CALL_LOG_FAILED });
    }
}

/**
 * POST a call-log entry recording a manager's no-contact hold release
 * (reason + timestamp). The reducer clears the hold on the same action.
 */
export function* releaseNoContactHoldSaga(action) {
    try {
        const sessionToken = yield select(state => state.OvertimeReducer?.callSheetData?.sessionToken) || localStorage.getItem('ot_sessionToken') || '';
        const requestDepartment = yield select((state) => state.OvertimeReducer?.callSheetData?.requestParams?.department || '');
        const callSheetId = yield select((state) => state.OvertimeReducer?.callSheetData?.callSheetId || action.votRequestId || '');
        const currentShiftType = yield select((state) => state.OvertimeReducer?.callSheetData?.callSheetWorkingShift?.current || '');
        const holdShiftType = action.shiftType || currentShiftType || '';

        yield call(postCallLog, {
            callType: 'SYSTEM',
            action: 'NOCONTACT_RELEASED',
            callNotes: action.reason || '',
            empId: action.employeeId || '',
            votRequestId: action.votRequestId || '',
            shiftType: action.shiftType || '',
            station: action.station || '',
            createdBy: action.createdBy || '',
            lastUpdated: action.lastUpdated || '',
            department: action.department || requestDepartment || '',
            sessionToken,
            startTime: action.startTime || '',
            endTime: action.endTime || '',
            workLocation: action.workLocation || '',
        });
        yield put({ type: overtimeConstants.POST_CALL_LOG_SUCCESS });

        // Re-fetch call log so GET_CALL_LOG_SUCCESS can confirm the released hold.
        if (callSheetId && action.employeeId) {
            yield put({
                type: overtimeConstants.GET_CALL_LOG,
                votId: callSheetId,
                empId: action.employeeId,
                shiftType: holdShiftType,
            });
        }
    } catch (error) {
        console.error('Release no-contact hold call log failed:', error);
        yield put({ type: overtimeConstants.POST_CALL_LOG_FAILED });
    }
}

/**
 * Fetches call log entries for a specific employee from the API.
 */
export function* getCallLogSaga(action) {
    try {
        const requestDepartment = yield select((state) => state.OvertimeReducer?.callSheetData?.requestParams?.department || '');
        const department = action?.department || requestDepartment || '';
        const response = yield call(getCallLog, { votId: action.votId, empId: action.empId, department });

        yield put({
            type: overtimeConstants.GET_CALL_LOG_SUCCESS,
            employeeId: action.empId,
            data: response.data,
        });
    } catch (error) {
        console.error('Get call log failed:', error);
        yield put({ type: overtimeConstants.GET_CALL_LOG_FAILED, data: error.message });
    }
}

export function* checkDuplicateShiftSaga(action) {
    try {
        const response = yield call(checkDuplicateShift, action.payload);
        yield put({
            type: overtimeConstants.CHECK_DUPLICATE_SHIFT_SUCCESS,
            payload: response?.data || response
        });
    } catch (error) {
        console.error('Check duplicate shift failed:', error);
        yield put({ 
            type: overtimeConstants.CHECK_DUPLICATE_SHIFT_FAILED, 
            data: error.message || 'Failed to check duplicate shift' 
        });
    }
}

/**
 * Fetches OT signups (assigned shifts) for a specific employee.
 */
export function* getOtSignupsSaga(action) {
    try {
        const requestParams = yield select((state) => state.OvertimeReducer?.callSheetData?.requestParams || {});
        const sessionToken = yield select((state) => state.OvertimeReducer?.callSheetData?.sessionToken) || localStorage.getItem('ot_sessionToken') || '';
        const employee = yield select((state) =>
            state.OvertimeReducer?.employees?.byId?.[action.employeeId] ||
            state.OvertimeReducer?.employees?.byId?.[action.empId] ||
            {}
        );
        const signupIds = yield select((state) =>
            state.OvertimeReducer?.shiftSignups?.byEmployeeId?.[action.employeeId] ||
            state.OvertimeReducer?.shiftSignups?.byEmployeeId?.[action.empId] ||
            []
        );
        const signupsById = yield select((state) => state.OvertimeReducer?.shiftSignups?.byId || {});
        const otShiftSignUps = signupIds
            .map((id) => signupsById[id])
            .filter(Boolean)
            .map((signup) => ({
                shiftName: signup.shiftName || signup.shiftType || '',
                restrictions: signup.restrictions ?? null,
                preferences: signup.preferences ?? '',
                autoSignUp: signup.autoSignUp ?? null,
                multiShifts: signup.multiShifts ?? null,
                startTime: signup.startTime || '',
                endTime: signup.endTime || '',
                workLocation: signup.workLocation || '',
                slotsOffered: signup.slotsOffered ?? signup.slots ?? null,
                slotsTaken: signup.slotsTaken ?? null,
                isEligible: signup.isEligible ?? null,
                notEligibleNotes: signup.notEligibleNotes ?? null,
                selectedDecision: signup.selectedDecision ?? null,
                isConfirmed: signup.isConfirmed ?? null,
                lastTimeStamp: signup.lastTimeStamp ?? null,
                transactionId: signup.transactionId ?? null,
                reqId: signup.reqId ?? null,
                wbStatus: signup.wbStatus ?? null,
                isCalled: signup.isCalled ?? null,
                sessionToken: signup.sessionToken ?? null,
                phoneNumber: signup.phoneNumber ?? null,
            }));
        const { station = '', otGroup = '', schedBidGroup = '', volOtDate = '' } = requestParams;
        const response = yield call(getOtSignups, {
            empId: action.empId,
            station,
            otGroup: employee.otGroup ?? otGroup,
            schedBidGroup: employee.schedBidGroup ?? schedBidGroup,
            volOtDate,
            sessionToken,
            otDayShifts: Array.isArray(employee.otDayShifts) ? employee.otDayShifts : [],
            otShiftSignUps,
        });
        yield put({
            type: overtimeConstants.GET_OT_SIGNUPS_SUCCESS,
            employeeId: action.employeeId,
            data: response.data,
        });
    } catch (error) {
        console.error('Get OT signups failed:', error);
        yield put({ type: overtimeConstants.GET_OT_SIGNUPS_FAILED, employeeId: action.employeeId, data: error.message });
    }
}

/**
 * Clears all persisted confirmed decisions from session storage when
 * the callsheet is exited (OVERTIME_CLEANUP).
 */
export function* overtimeCleanupSaga() {
    clearAllPersistedDecisions();
}
